// Choose a recording — or add one — for whatever needs one.
//
// Kevin, 2026-08-27: "the drop down should have a 'Upload new recording'. The modal card also says
// greeting, when it is Recording selector. This should be something that is sharable on other pages
// so create a common, generic modal form for selecting recordings would be helpful."
//
// THREE THINGS THIS FIXES, and they are all about discovery:
//
//  1. UPLOAD IS IN THE LIST. It was a separate section below the select — so the operator's eye
//     found "Choose…", found nothing to choose, and stopped. Putting "Upload a new recording…" in
//     the dropdown means the way forward is where they are already looking.
//  2. IT IS NAMED FOR WHAT IT IS. The modal said "Greeting" because the first caller happened to be
//     a greeting. It picks a recording; the caller supplies the context as a subtitle.
//  3. THE FIELDS ANNOUNCE THEMSELVES. Name had an `aria-label` and no visible one, so its only hint
//     was a placeholder — which reads as a value already filled in. The file input was a browser
//     default. Now both are labelled, and the file area is a target you can click, tab to or drop
//     onto.
//
// GENERIC BY CONTRACT, not by accident:
//   `onPreview` optional — supply it for a two-step flow (Continue, then Apply) where the server has
//   something to say before the change lands; omit it and the modal is one button.
//   `kind` is passed to the upload so a surface counts against its own cap.
//
// NO IMPLEMENTATION WORDS. Kevin, same day: "they don't need to know that a PBX even exists." An
// operator manages a customer's phone service; where the audio physically lives is not their
// problem, and it was on screen.

import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { encodeWavFromBlob } from '../../lib/wavEncode.js';
import FormModal from './FormModal.jsx';

const UPLOAD = '__upload';

export default function RecordingPickerModal({
  title = 'Choose a recording',
  subtitle,
  // `[{ id, name, source, disabled?, warn? }]`, or null when the list could not be loaded — the two
  // are different and only one of them is worth retrying.
  //
  // `warn` is shown the moment that option is chosen. It is for an option that is legitimate but
  // will probably not work, which is a different thing from `disabled`: disabling a customer's own
  // recording says something permanent about the recording, when the fault is ours and temporary.
  //
  // `source` IS THE CALLER'S WORD and travels back out untouched. It exists because these ids live
  // in different spaces: a recording already on the phone system is not the same id as one in the
  // customer's library, and the caller is the only thing that knows which field each belongs in.
  // Without it, a freshly uploaded recording could not be selected at all — which is exactly what
  // Kevin hit: "after uploading, it should select the uploaded recording."
  recordings,
  // Where an upload lands, so the new recording can be selected the moment it exists.
  uploadSource = 'library',
  subscriberId,
  kind = 'greeting',
  // `(option) => { confirm?: [{id, why}], blockers?: [{id, why}], error? }` — the whole option,
  // including its `source`, because only the caller knows what to do with each kind.
  onPreview = null,
  // `(option) => Promise<void>` — the change itself.
  onSelect,
  onClose,
}) {
  // Keyed `source:id`, because the two id spaces overlap — host recording 2 and library recording 2
  // are different audio.
  const [choice, setChoice] = useState('');
  const [extra, setExtra] = useState([]);   // uploaded here, selectable immediately
  const [preview, setPreview] = useState(null);
  const [name, setName] = useState('');
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const qc = useQueryClient();

  const uploading = choice === UPLOAD;
  const loadFailed = recordings == null;
  const options = [...extra, ...(recordings || []).filter((r) => !r.disabled)];
  const keyOf = (r) => `${r.source || uploadSource}:${r.id}`;
  const chosen = options.find((r) => keyOf(r) === choice) || null;

  const upload = useMutation({
    mutationFn: async () => {
      // Converted here so whatever the customer sent works; the server checks again on arrival.
      const wav = await encodeWavFromBlob(file);
      const form = new FormData();
      form.append('audio', new Blob([wav], { type: 'audio/wav' }), 'recording.wav');
      form.append('name', name.trim());
      form.append('kind', kind);
      return api.post(`/subscribers/${subscriberId}/recordings`, form);
    },
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['subscriber', String(subscriberId), 'recordings'] });
      const id = created?.id ?? null;
      const saved = name.trim();
      setFile(null); setName('');
      if (id != null) {
        // SELECT WHAT WAS JUST UPLOADED. Saving and then leaving the operator back at "Choose…" is
        // the step nobody would guess they still had to take.
        const row = { id, name: saved, source: uploadSource };
        setExtra((prev) => [row, ...prev]);
        setChoice(`${uploadSource}:${id}`);
        setNote(`Saved “${saved}” and selected it.`);
      } else {
        setNote(`Saved “${saved}”.`);
      }
    },
    onError: (e) => setNote(e?.body?.error || e?.message || 'That file could not be saved.'),
  });

  const commit = async () => {
    setBusy(true); setPreview(null); setNote(null);
    try {
      // A CALLER-SUPPLIED PREVIEW IS STILL RUN — it is where the server's refusals come from — but
      // it is not a button any more. Blockers stop the save and are shown; anything else proceeds.
      if (onPreview) {
        const p = await onPreview(chosen);
        if (p?.error || p?.blockers?.length) { setBusy(false); setPreview(p); return; }
      }
      await onSelect(chosen);
      onClose();
    } catch (e) {
      setBusy(false);
      setPreview({ error: e?.body?.error || e?.message || 'That change failed.' });
    }
  };

  const take = (f) => { if (f) { setFile(f); setNote(null); } };
  const missing = !name.trim() ? 'a name' : (!file ? 'a file' : null);

  // TWO BUTTONS, EACH MEANING ONE THING. Kevin, 2026-08-27: "there should be an 'Upload' button,
  // then a save the selected button."
  //
  // The footer used to swap identity — Save recording, or Continue, or Use this recording —
  // depending on state, so the modal's primary action changed under the operator. Upload is a
  // sub-action and lives inline with the fields it needs; the footer is always Save, and always
  // saves the selection.
  const footer = (
    <button type="button" onClick={commit} disabled={!chosen || busy}
      className="rounded-md bg-red-700 px-3 py-2 text-sm font-medium text-white hover:bg-red-800 disabled:opacity-40">
      {busy ? 'Saving…' : 'Save'}
    </button>
  );

  return (
    <FormModal title={title} subtitle={subtitle} onClose={onClose} footer={footer}>
      <label className="block text-sm font-medium text-gray-900" htmlFor="rec-pick">Recording</label>
      <select id="rec-pick" value={choice}
        onChange={(e) => { setChoice(e.target.value); setPreview(null); setNote(null); }}
        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm">
        <option value="">Choose…</option>
        {options.map((r) => (
          <option key={keyOf(r)} value={keyOf(r)}>{r.name || `Recording ${r.id}`}</option>
        ))}
        {/* THE WAY FORWARD, where the eye already is. */}
        {subscriberId != null && <option value={UPLOAD}>Upload a new recording…</option>}
      </select>

      {/* THE CHOSEN OPTION'S OWN CAVEAT, the moment it is chosen. A caller can mark an option with a
          `warn` — it stays selectable, but nobody has to press Save to find out. */}
      {chosen?.warn && (
        <p className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
          {chosen.warn}
        </p>
      )}
      {loadFailed && <p className="mt-1 text-xs text-amber-800">Could not load recordings. Try again.</p>}
      {!loadFailed && !options.length && !uploading && (
        <p className="mt-1 text-xs text-gray-500">No recordings yet — choose “Upload a new recording”.</p>
      )}

      {uploading && (
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-900" htmlFor="rec-name">Name</label>
          <input id="rec-name" value={name} maxLength={60}
            onChange={(e) => { setName(e.target.value); setNote(null); }}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />

          <div className="mt-3 text-sm font-medium text-gray-900">Audio file</div>
          {/* A TARGET, not a browser default: clickable, focusable, and a drop zone — the file is
              usually already in a folder or an email, and dragging is the shortest path. */}
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); take(e.dataTransfer?.files?.[0]); }}
            className={`mt-1 w-full rounded border-2 border-dashed px-3 py-6 text-center text-xs ${
              dragging ? 'border-blue-500 bg-blue-50 text-blue-800' : 'border-gray-300 bg-gray-50 text-gray-600'
            }`}
          >
            {file
              ? <><span className="font-medium text-gray-900">{file.name}</span> — click or drop to replace</>
              : <>Drop a file here, or <span className="font-medium text-gray-900">click to choose one</span></>}
          </button>
          <input ref={fileRef} type="file" accept="audio/*" className="hidden" aria-label="Audio file"
            onChange={(e) => take(e.target.files?.[0])} />

          <div className="mt-3 flex items-center gap-2">
            <button type="button" disabled={!!missing || upload.isPending} onClick={() => upload.mutate()}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40">
              {upload.isPending ? 'Uploading…' : 'Upload'}
            </button>
            {/* WHY IT IS DISABLED, rather than leaving somebody to guess which field is empty. */}
            {missing && <span className="text-xs text-gray-500">Needs {missing}.</span>}
          </div>
        </div>
      )}

      {preview && (
        <div className="mt-3 rounded border border-gray-200 bg-gray-50 p-2">
          {preview.error || preview.blockers?.length ? (
            <ul className="list-disc space-y-0.5 pl-4 text-xs text-red-700">
              {(preview.blockers?.length ? preview.blockers : [{ why: preview.error }])
                .map((b, i) => <li key={i}>{b.why}</li>)}
            </ul>
          ) : (
            // The server's own words: it is the side that knows what is there now.
            (preview.confirm || []).map((c) => (
              <p key={c.id} className="text-xs text-gray-800">{c.why}</p>
            ))
          )}
        </div>
      )}

      {note && <p className="mt-2 text-xs text-gray-700">{note}</p>}
    </FormModal>
  );
}
